import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Drawer,
  drawerClasses,
  type DrawerProps,
  IconButton,
  styled,
  useTheme,
} from '@mui/material';
import { type PropsWithChildren, ReactNode } from 'react';

import {
  ActiveCase,
  ActiveCaseCard,
  caseStatusMap,
  CaseType,
  ENOPCaseStatus,
  ERouteType,
  getTrialIcon,
  NewCase,
  NewCaseCard,
} from '#entities';
import {
  GearIcon,
  getIsItalyRegionId,
  ICONS,
  MAX_DRAWER_WIDTH,
  MIN_DRAWER_WIDTH,
  ORGAN_COLORS,
  REACT_APP_LOGISTICS_BASE_URL,
} from '#shared';

import { CaseLegs } from '../../CaseLegs';

import { ExternalCaseLink } from './ExternalCaseLink';

import type { CaseLogisticsResponse } from '#entities';

const DrawerRoot = styled(Drawer)(
  ({ theme: { spacing, palette, utils, breakpoints } }) => ({
    background: 'none',
    width: utils.clamp(MIN_DRAWER_WIDTH, MAX_DRAWER_WIDTH),
    flexShrink: 0,

    [breakpoints.down('sm')]: {
      width: '100%',
    },

    [`& .${drawerClasses.paper}`]: {
      width: utils.clamp(MIN_DRAWER_WIDTH, MAX_DRAWER_WIDTH),
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: palette.background.default,
      boxSizing: 'border-box',
      border: 'none',
      borderRadius: spacing(0.5),
      pointerEvents: 'auto',

      [breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  }),
);

const Container = styled(Box)(({ theme: { spacing, palette } }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  gap: spacing(2),
  padding: spacing(2),
  color: 'var(--primaryColor)',
  fontFamily: "'JetBrains Mono', monospace",
  backgroundColor: palette.background.default,
}));

const Header = styled(Box)(({ theme: { utils } }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: utils.clamp(16, 24),
  fontWeight: 500,
}));

const CloseButton = styled(IconButton)(() => ({
  alignSelf: 'flex-end',
  background: 'none',
}));

const CaseLabel = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  display: 'inline-block',
}));

export interface CaseDetailsDrawerProps extends Omit<DrawerProps, 'children'> {
  caseData: ActiveCase | NewCase | null;
  selectedCaseType: CaseType | null;
  onClose?: () => void;
  children?: ReactNode;
}

export const CaseDetailsDrawer = ({
  onClose,
  open,
  children,
  caseData,
  selectedCaseType,
  ...drawerProps
}: PropsWithChildren<CaseDetailsDrawerProps>) => {
  const { palette, utils } = useTheme();
  const organName = caseData?.organ?.name ?? '';
  const redirectLink = `${REACT_APP_LOGISTICS_BASE_URL}/case/${caseData?.id}`;

  const isItalian = getIsItalyRegionId(caseData?.serviceRegionId ?? '');
  const isReturningStatus = caseData
    ? (caseData as ActiveCase).status === ENOPCaseStatus.ReturningToBase
    : false;

  let cardStatus: ENOPCaseStatus | '' = '';

  if (caseData) {
    if (selectedCaseType === CaseType.ActiveCase) {
      cardStatus = (caseData as ActiveCase).status;
    } else if (selectedCaseType === CaseType.NewCase) {
      cardStatus = (caseData as NewCase).oscStatus;
    }
  }

  const textColor = isReturningStatus
    ? palette.common.white
    : selectedCaseType === CaseType.ActiveCase
      ? ORGAN_COLORS[organName] || 'inherit'
      : palette.orange[500];

  const activeCaseData =
    selectedCaseType === CaseType.ActiveCase && caseData
      ? (caseData as ActiveCase)
      : null;

  const newCaseData =
    selectedCaseType === CaseType.NewCase && caseData
      ? (caseData as NewCase)
      : null;

  const OrganIcon = ICONS[organName] ?? GearIcon;
  const showCaseLink = caseData?.id && caseData?.label;
  const organIconSize = utils.clamp(16, 24);
  const TrialIcon = caseData?.trialProgram
    ? (getTrialIcon(caseData?.trialProgram.name) ?? null)
    : null;

  const route = activeCaseData?.logistics?.routes.find(
    r => r.routeType === ERouteType.Main,
  );

  return (
    <DrawerRoot
      open={open}
      onClose={onClose}
      anchor="right"
      ModalProps={{
        keepMounted: true,
      }}
      {...drawerProps}
    >
      <Container>
        <CloseButton onClick={onClose}>
          <CloseIcon sx={{ color: palette.common.white }} />
        </CloseButton>
        <Header sx={{ color: textColor }}>
          <Box display="flex" alignItems="center">
            <OrganIcon height={organIconSize} />
            {showCaseLink ? (
              <ExternalCaseLink
                href={redirectLink}
                underline="always"
                sx={{ color: textColor, textDecorationColor: textColor }}
              >
                {caseData?.label}
              </ExternalCaseLink>
            ) : (
              <CaseLabel sx={{ color: textColor }}>
                {caseData?.label ?? 'N/A'}
              </CaseLabel>
            )}
          </Box>
          {TrialIcon && <TrialIcon />}
          <span>{caseStatusMap[cardStatus]}</span>
        </Header>

        {activeCaseData && (
          <>
            <ActiveCaseCard
              activeCase={activeCaseData}
              sx={{ width: '100%', pointerEvents: 'none' }}
            />
            <CaseLegs
              isItalian={isItalian}
              route={route ?? null}
              locations={
                activeCaseData?.logistics
                  ?.locations as CaseLogisticsResponse['locations']
              }
              resources={
                activeCaseData?.logistics
                  ?.resources as CaseLogisticsResponse['resources']
              }
            />
          </>
        )}
        {newCaseData && (
          <NewCaseCard
            data={newCaseData}
            sx={{ width: '100%', pointerEvents: 'none' }}
          />
        )}
        {children}
      </Container>
    </DrawerRoot>
  );
};
